/**
 * Shared shell for Cornerstone v2 preview screens.
 *
 * Provides:
 *  • Full-screen mount (bypasses admin/portal sidebar)
 *  • Force-activated `.theme-v2` while mounted (auto-reverts on unmount unless
 *    user opted-in globally via ?theme=v2)
 *  • Consistent top bar (title + back button) and fixed bottom navigation
 *
 * Screens using this shell:
 *  /preview/parent               ParentDashboardV2
 *  /preview/parent/schedule      ScheduleV2
 *  /preview/parent/invoices      InvoicesV2
 *  /preview/parent/messages      MessagesV2
 *  /preview/parent/more          MoreV2
 */
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, Calendar, MessageSquare, Wallet, MoreHorizontal, ChevronLeft,
} from "lucide-react";

export function useV2Theme() {
  useEffect(() => {
    document.documentElement.classList.add("theme-v2");
    return () => {
      const persistent = localStorage.getItem("cornerstone-theme") === "v2";
      if (!persistent) document.documentElement.classList.remove("theme-v2");
    };
  }, []);
}

export function PreviewShell({ title, subtitle, children, backTo }) {
  const nav = useNavigate();
  useV2Theme();
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white text-[color:var(--v2-text)] pb-24"
         data-testid="v2-preview-shell">
      {(title || backTo) && (
        <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[color:var(--v2-border)]">
          <div className="mx-auto max-w-md sm:max-w-2xl lg:max-w-4xl flex items-center gap-3 px-4 py-3">
            <button onClick={() => (backTo ? nav(backTo) : nav(-1))}
                    className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--v2-soft-grey)]"
                    data-testid="v2-back-btn">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-lg font-semibold truncate">{title}</h1>
              {subtitle && <p className="text-xs text-[color:var(--v2-text-muted)] truncate">{subtitle}</p>}
            </div>
          </div>
        </header>
      )}
      <div className="mx-auto max-w-md sm:max-w-2xl lg:max-w-4xl px-4 sm:px-5 pt-4">
        {children}
      </div>
      <BottomNav />
    </div>
  );
}

export function BottomNav() {
  const items = [
    { to: "/preview/parent", icon: Home, label: "Home" },
    { to: "/preview/parent/schedule", icon: Calendar, label: "Schedule" },
    { to: "/preview/parent/messages", icon: MessageSquare, label: "Messages" },
    { to: "/preview/parent/invoices", icon: Wallet, label: "Payments" },
    { to: "/preview/parent/more", icon: MoreHorizontal, label: "More" },
  ];
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[color:var(--v2-border)] shadow-[0_-1px_8px_rgba(20,33,61,0.06)]"
         data-testid="v2-bottom-nav">
      <div className="mx-auto max-w-md grid grid-cols-5 py-2">
        {items.map((it) => {
          const active = path === it.to;
          const Icon = it.icon;
          return (
            <Link key={it.label} to={it.to}
                  className={"flex flex-col items-center gap-0.5 py-1 " + (active ? "text-[color:var(--v2-primary)]" : "text-[color:var(--v2-text-muted)]")}
                  data-testid={`v2-nav-${it.label.toLowerCase()}`}>
              <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} />
              <span className={"text-[10px] " + (active ? "font-semibold" : "font-medium")}>{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/* Small primitives used across preview screens */
export function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  const map = {
    paid: ["bg-emerald-50", "text-emerald-700"],
    confirmed: ["bg-emerald-50", "text-emerald-700"],
    approved: ["bg-emerald-50", "text-emerald-700"],
    pending: ["bg-amber-50", "text-amber-700"],
    unpaid: ["bg-rose-50", "text-rose-700"],
    overdue: ["bg-rose-50", "text-rose-700"],
    rejected: ["bg-rose-50", "text-rose-700"],
  };
  const [bg, txt] = map[s] || ["bg-slate-100", "text-slate-700"];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${bg} ${txt}`}>
      {String(status).charAt(0).toUpperCase() + String(status).slice(1)}
    </span>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <div className={`rounded-xl border border-[color:var(--v2-border)] bg-white shadow-[0_1px_2px_rgba(20,33,61,0.04)] ${className}`} {...rest}>
      {children}
    </div>
  );
}
